import api from './api';

/**
 * PredictionService – Tippspiel-System (Port der Flutter PredictionService)
 *
 * Backend-Routen (verifiziert in dartcloud-backend/src/core/routes/prediction-routes.js
 * + src/core/api/uris/prediction-uris.js + prediction-controller.js):
 *
 * - GET    /api/predictions/games                       (public)  → { status, results, total, page, totalPages, data: { games } }
 * - GET    /api/predictions/my-games                    (auth)    → { status, results, data: { myGames: [{ game, participation }] } }
 * - GET    /api/predictions/games/:id                   (opt-auth)→ { status, data: { game, participation, isRegistered } }
 * - POST   /api/predictions/games/:id/register          (auth)    → 201 { status, message, data: { participant } } | Body: { predictionUsername }
 * - DELETE /api/predictions/games/:id/unregister        (auth)    → 200 { status, message }
 * - POST   /api/predictions/games/:id/predict/match     (auth)    → { status, message, data: { participant } } | Body: { eventId, predictedWinner, predictedScore }
 * - POST   /api/predictions/games/:id/predict/semifinal (auth)    → { status, message, data: { participant } } | Body: { playerIds: string[4] }
 * - POST   /api/predictions/games/:id/predict/champion  (auth)    → { status, message, data: { participant } } | Body: { playerId }
 * - GET    /api/predictions/games/:id/my-predictions    (auth)    → { status, data: { participant } }
 * - GET    /api/predictions/games/:id/my-stats          (auth)    → { status, data: { stats } }
 * - GET    /api/predictions/games/:id/leaderboard       (public)  → { status, data: { rankings, totalEntries, page, totalPages, limit } }
 * - GET    /api/predictions/games/:id/fixtures          (public)  → { status, results, data: { fixtures, gameDates, players, totalFixtures } }
 * - GET    /api/predictions/games/:id/results           (public)  → { status, results, data: { results } }
 * - GET    /api/predictions/games/:id/special-deadline  (public)  → { status, data: { firstMatchTime, autoDeadline, semiFinalDeadline, championDeadline, canPredict } }
 *
 * `:id` darf MongoDB-ObjectId ODER Slug sein (Backend: findGameByIdOrSlug).
 * Alle Felder defensiv/nullable parsen – Backend-Responses sind nicht garantiert vollständig!
 */

// ============================================================
// Interfaces (Struktur aus Backend-Controller + Flutter-Models)
// ============================================================

export interface PredictionPoints {
  exactScore?: number | null;
  correctWinner?: number | null;
}

export interface SpecialPredictionsSettings {
  semiFinalEnabled?: boolean | null;
  semiFinalPoints?: number | null;
  semiFinalDeadline?: string | null;
  championEnabled?: boolean | null;
  championPoints?: number | null;
  championDeadline?: string | null;
}

export interface PredictionGameSettings {
  matchPredictionsEnabled?: boolean | null;
  matchDeadlineHours?: number | null;
  points?: PredictionPoints | null;
  specialPredictions?: SpecialPredictionsSettings | null;
}

export interface PredictionGameStats {
  totalParticipants?: number | null;
  totalPredictions?: number | null;
  evaluatedMatches?: number | null;
}

export interface PredictionGameTournament {
  ref?: any;
  flashliveId?: string | null;
  stageId?: string | null;
  name?: string | null;
}

export interface PredictionPrize {
  id?: string | null;
  _id?: string | null;
  rank?: number | null;
  title?: string | null;
  subtitle?: string | null;
  description?: string | null;
  image?: string | null;
}

export interface PredictionSponsor {
  id?: string | null;
  _id?: string | null;
  title?: string | null;
  subtitle?: string | null;
  description?: string | null;
  image?: string | null;
  linkUrl?: string | null;
  order?: number | null;
  active?: boolean | null;
}

export interface PredictionGame {
  _id?: string | null;
  id?: string | null;
  name?: string | null;
  slug?: string | null;
  description?: string | null;
  tournament?: PredictionGameTournament | null;
  registrationStart?: string | null;
  registrationEnd?: string | null;
  gameStart?: string | null;
  gameEnd?: string | null;
  settings?: PredictionGameSettings | null;
  status?: string | null; // draft | open | running | finished
  stats?: PredictionGameStats | null;
  prizes?: PredictionPrize[] | null;
  lotteryPrizes?: PredictionPrize[] | null;
  sponsors?: PredictionSponsor[] | null;
  heroSlides?: any[] | null;
  randomizeSponsors?: boolean | null;
  termsUrl?: string | null;
  /** Nur für Admins sichtbar/anmeldbar (Server filtert für Nicht-Admins) */
  adminOnly?: boolean | null;
  isActive?: boolean | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface PredictionPlayerInfo {
  id?: string | null;
  playerId?: string | null;
  name?: string | null;
  playerName?: string | null;
  image?: string | null;
  playerImage?: string | null;
  country?: string | null;
}

export interface PredictionScore {
  home?: number | null;
  away?: number | null;
}

export interface MatchPrediction {
  event?: string | { _id?: string | null } | null;
  flashliveEventId?: string | null;
  round?: string | null;
  homePlayer?: PredictionPlayerInfo | null;
  awayPlayer?: PredictionPlayerInfo | null;
  predictedWinner?: number | null; // 1 = home, 2 = away
  predictedScore?: PredictionScore | null;
  actualWinner?: number | null;
  actualScore?: PredictionScore | null;
  points?: number | null;
  isEvaluated?: boolean | null;
  submittedAt?: string | null;
  evaluatedAt?: string | null;
}

export interface SemiFinalPrediction {
  players?: PredictionPlayerInfo[] | null;
  actualPlayers?: string[] | null;
  correctCount?: number | null;
  points?: number | null;
  isEvaluated?: boolean | null;
  submittedAt?: string | null;
}

export interface ChampionPrediction {
  playerId?: string | null;
  playerName?: string | null;
  playerImage?: string | null;
  actualChampion?: string | null;
  isCorrect?: boolean | null;
  points?: number | null;
  isEvaluated?: boolean | null;
  submittedAt?: string | null;
}

export interface SpecialPredictions {
  semiFinal?: SemiFinalPrediction | null;
  champion?: ChampionPrediction | null;
}

export interface PredictionParticipant {
  _id?: string | null;
  predictionGame?: any;
  user?: any;
  predictionUsername?: string | null;
  matchPredictions?: MatchPrediction[] | null;
  specialPredictions?: SpecialPredictions | null;
  totalPoints?: number | null;
  rank?: number | null;
  registeredAt?: string | null;
  lastPredictionAt?: string | null;
}

/** Kompakte Teilnahme-Info aus GET /my-games */
export interface MyGameParticipation {
  totalPoints?: number | null;
  rank?: number | null;
  matchPredictionsCount?: number | null;
  lastPredictionAt?: string | null;
}

export interface MyGameItem {
  game: PredictionGame;
  participation?: MyGameParticipation | null;
}

export interface GameDetailResponse {
  game: PredictionGame | null;
  participation: PredictionParticipant | null;
  isRegistered: boolean;
}

export interface LeaderboardMatchStats {
  total?: number | null;
  evaluated?: number | null;
  correct?: number | null;
  exactScores?: number | null;
}

export interface LeaderboardSpecialStats {
  semiFinalCorrect?: number | null;
  championCorrect?: boolean | null;
}

export interface LeaderboardEntry {
  rank?: number | null;
  oderId?: string | null; // UUID des Users (für öffentliche Tipp-Details)
  predictionUsername?: string | null;
  totalPoints?: number | null;
  matchPredictions?: LeaderboardMatchStats | null;
  specialPredictions?: LeaderboardSpecialStats | null;
}

export interface PredictionLeaderboard {
  rankings: LeaderboardEntry[];
  totalEntries: number;
  page: number;
  totalPages: number;
  limit: number;
}

/** ProEvent-Struktur (nested Score!) – wie /api/pro/events */
export interface FixtureParticipant {
  id?: string | null;
  name?: string | null;
  shortName?: string | null;
  image?: string | null;
  country?: any;
}

export interface FixtureScoreDetail {
  current?: number | null;
  full?: number | null;
}

export interface PredictionFixture {
  _id?: string | null;
  flashliveEventId?: string | null;
  startTime?: string | null;
  startUtime?: number | null;
  status?: string | null; // scheduled | live | finished | ...
  round?: string | null;
  homeParticipant?: FixtureParticipant | null;
  awayParticipant?: FixtureParticipant | null;
  score?: { home?: FixtureScoreDetail | null; away?: FixtureScoreDetail | null } | null;
  winner?: number | null; // 1 = home, 2 = away
  matchFormat?: { wd?: number | null; bestOf?: number | null } | null;
  /** FlashLive-Statistik-Einträge (permanent gecacht bei beendeten Events) */
  stats?: any[] | null;
  tournament?: { flashliveId?: string | null; name?: string | null } | null;
}

export interface TournamentPlayer {
  id?: string | null;
  name?: string | null;
  image?: string | null;
  country?: string | null;
}

export interface FixturesData {
  fixtures: PredictionFixture[];
  gameDates: string[];
  players: TournamentPlayer[];
  totalFixtures: number;
}

export interface SpecialDeadlineInfo {
  firstMatchTime?: string | null;
  autoDeadline?: string | null;
  semiFinalDeadline?: string | null;
  championDeadline?: string | null;
  canPredict?: boolean | null;
}

export interface MyStats {
  totalPoints?: number | null;
  rank?: number | null;
  matchPredictions?: LeaderboardMatchStats | null;
  specialPredictions?: {
    semiFinalSubmitted?: boolean | null;
    semiFinalCorrect?: number | null;
    championSubmitted?: boolean | null;
    championCorrect?: boolean | null;
  } | null;
}

export interface GameStatusInfo {
  key: 'upcoming' | 'active' | 'finished' | 'draft';
  label: string;
  /** Tailwind-kompatible Farben fürs Status-Badge */
  color: string;
  bgColor: string;
}

// ============================================================
// Service
// ============================================================

class PredictionService {
  // ---------- API: Games ----------

  /** Alle aktiven Tippspiele (public) */
  async getAllGames(): Promise<PredictionGame[]> {
    try {
      const response = await api.get('/api/predictions/games');
      const games = response?.data?.data?.games;
      return Array.isArray(games) ? games : [];
    } catch (error) {
      console.error('[predictionService] getAllGames fehlgeschlagen:', error);
      return [];
    }
  }

  /** Tippspiele, bei denen der eingeloggte User registriert ist */
  async getMyGames(): Promise<MyGameItem[]> {
    try {
      const response = await api.get('/api/predictions/my-games');
      const myGames = response?.data?.data?.myGames;
      if (!Array.isArray(myGames)) return [];
      return myGames
        .filter((item: any) => item && item.game)
        .map((item: any) => ({
          game: item.game as PredictionGame,
          participation: item.participation ?? null,
        }));
    } catch (error) {
      console.error('[predictionService] getMyGames fehlgeschlagen:', error);
      return [];
    }
  }

  /**
   * Einzelnes Tippspiel per ID oder Slug.
   * Mit gültigem dc-sign Header liefert das Backend zusätzlich participation/isRegistered.
   */
  async getGameById(idOrSlug: string): Promise<GameDetailResponse | null> {
    try {
      const response = await api.get(`/api/predictions/games/${encodeURIComponent(idOrSlug)}`);
      const data = response?.data?.data;
      if (!data || !data.game) return null;
      return {
        game: data.game as PredictionGame,
        participation: data.participation ?? null,
        isRegistered: data.isRegistered === true,
      };
    } catch (error) {
      console.error('[predictionService] getGameById fehlgeschlagen:', error);
      return null;
    }
  }

  // ---------- API: Registrierung ----------

  /** Registrierung für ein Tippspiel – wirft Error mit deutscher Backend-Message */
  async registerForGame(idOrSlug: string, predictionUsername: string): Promise<PredictionParticipant> {
    try {
      const response = await api.post(
        `/api/predictions/games/${encodeURIComponent(idOrSlug)}/register`,
        { predictionUsername }
      );
      return (response?.data?.data?.participant ?? {}) as PredictionParticipant;
    } catch (error: any) {
      throw new Error(this.extractErrorMessage(error, 'Registrierung fehlgeschlagen'));
    }
  }

  /** Abmeldung vom Tippspiel (nur möglich, wenn noch keine Tipps abgegeben wurden) */
  async unregisterFromGame(idOrSlug: string): Promise<void> {
    try {
      await api.delete(`/api/predictions/games/${encodeURIComponent(idOrSlug)}/unregister`);
    } catch (error: any) {
      throw new Error(this.extractErrorMessage(error, 'Abmeldung fehlgeschlagen'));
    }
  }

  // ---------- API: Tipps ----------

  /** Match-Tipp abgeben/ändern. predictedWinner: 1 = Heim, 2 = Auswärts */
  async submitMatchPrediction(
    idOrSlug: string,
    eventId: string,
    predictedWinner: number,
    predictedScore?: PredictionScore | null
  ): Promise<PredictionParticipant> {
    try {
      const response = await api.post(
        `/api/predictions/games/${encodeURIComponent(idOrSlug)}/predict/match`,
        { eventId, predictedWinner, predictedScore: predictedScore ?? null }
      );
      return (response?.data?.data?.participant ?? {}) as PredictionParticipant;
    } catch (error: any) {
      throw new Error(this.extractErrorMessage(error, 'Tipp konnte nicht gespeichert werden'));
    }
  }

  /** Halbfinal-Tipp: genau 4 Spieler-IDs */
  async submitSemiFinalPrediction(idOrSlug: string, playerIds: string[]): Promise<PredictionParticipant> {
    try {
      const response = await api.post(
        `/api/predictions/games/${encodeURIComponent(idOrSlug)}/predict/semifinal`,
        { playerIds }
      );
      return (response?.data?.data?.participant ?? {}) as PredictionParticipant;
    } catch (error: any) {
      throw new Error(this.extractErrorMessage(error, 'Halbfinale-Tipp konnte nicht gespeichert werden'));
    }
  }

  /** Champion-Tipp: 1 Spieler-ID (Backend löst den Namen selbst auf) */
  async submitChampionPrediction(idOrSlug: string, playerId: string): Promise<PredictionParticipant> {
    try {
      const response = await api.post(
        `/api/predictions/games/${encodeURIComponent(idOrSlug)}/predict/champion`,
        { playerId }
      );
      return (response?.data?.data?.participant ?? {}) as PredictionParticipant;
    } catch (error: any) {
      throw new Error(this.extractErrorMessage(error, 'Sieger-Tipp konnte nicht gespeichert werden'));
    }
  }

  // ---------- API: Meine Daten ----------

  async getMyPredictions(idOrSlug: string): Promise<PredictionParticipant | null> {
    try {
      const response = await api.get(
        `/api/predictions/games/${encodeURIComponent(idOrSlug)}/my-predictions`
      );
      return (response?.data?.data?.participant ?? null) as PredictionParticipant | null;
    } catch (error) {
      // 404 = nicht registriert → kein harter Fehler
      return null;
    }
  }

  async getMyStats(idOrSlug: string): Promise<MyStats | null> {
    try {
      const response = await api.get(`/api/predictions/games/${encodeURIComponent(idOrSlug)}/my-stats`);
      return (response?.data?.data?.stats ?? null) as MyStats | null;
    } catch (error) {
      return null;
    }
  }

  // ---------- API: Public Game-Daten ----------

  async getLeaderboard(idOrSlug: string, page = 1, limit = 20): Promise<PredictionLeaderboard | null> {
    try {
      const response = await api.get(
        `/api/predictions/games/${encodeURIComponent(idOrSlug)}/leaderboard?page=${page}&limit=${limit}`
      );
      const data = response?.data?.data;
      if (!data) return null;
      return {
        rankings: Array.isArray(data.rankings) ? data.rankings : [],
        totalEntries: typeof data.totalEntries === 'number' ? data.totalEntries : 0,
        page: typeof data.page === 'number' ? data.page : page,
        totalPages: typeof data.totalPages === 'number' ? data.totalPages : 1,
        limit: typeof data.limit === 'number' ? data.limit : limit,
      };
    } catch (error) {
      console.error('[predictionService] getLeaderboard fehlgeschlagen:', error);
      return null;
    }
  }

  /** Anstehende Spiele + Spieler-Liste (für Spezial-Tipps) */
  async getTournamentFixtures(idOrSlug: string, options?: { date?: string; all?: boolean }): Promise<FixturesData | null> {
    try {
      const params: string[] = [];
      if (options?.date) params.push(`date=${encodeURIComponent(options.date)}`);
      if (options?.all) params.push('all=true');
      const query = params.length > 0 ? `?${params.join('&')}` : '';
      const response = await api.get(
        `/api/predictions/games/${encodeURIComponent(idOrSlug)}/fixtures${query}`
      );
      const data = response?.data?.data;
      if (!data) return null;
      const fixtures = Array.isArray(data.fixtures) ? data.fixtures : [];
      return {
        fixtures,
        gameDates: Array.isArray(data.gameDates) ? data.gameDates.map((d: any) => String(d)) : [],
        players: Array.isArray(data.players) ? data.players : [],
        totalFixtures: typeof data.totalFixtures === 'number' ? data.totalFixtures : fixtures.length,
      };
    } catch (error) {
      console.error('[predictionService] getTournamentFixtures fehlgeschlagen:', error);
      return null;
    }
  }

  /** Beendete Spiele des Turniers */
  async getTournamentResults(idOrSlug: string): Promise<PredictionFixture[]> {
    try {
      const response = await api.get(`/api/predictions/games/${encodeURIComponent(idOrSlug)}/results`);
      const results = response?.data?.data?.results;
      return Array.isArray(results) ? results : [];
    } catch (error) {
      console.error('[predictionService] getTournamentResults fehlgeschlagen:', error);
      return [];
    }
  }

  /** Deadline für Spezial-Tipps (1h vor erstem Turnierspiel bzw. explizite Deadline) */
  async getSpecialPredictionsDeadline(idOrSlug: string): Promise<SpecialDeadlineInfo | null> {
    try {
      const response = await api.get(
        `/api/predictions/games/${encodeURIComponent(idOrSlug)}/special-deadline`
      );
      return (response?.data?.data ?? null) as SpecialDeadlineInfo | null;
    } catch (error) {
      console.error('[predictionService] getSpecialPredictionsDeadline fehlgeschlagen:', error);
      return null;
    }
  }

  // ============================================================
  // Helper
  // ============================================================

  /** Identifier fürs Routing (Slug bevorzugt, wie Flutter) */
  getGameIdentifier(game: PredictionGame | null | undefined): string {
    if (!game) return '';
    return game.slug || game._id || game.id || '';
  }

  /** Status-Helper mit deutschen Labels */
  getGameStatusInfo(game: PredictionGame | null | undefined): GameStatusInfo {
    const status = game?.status || '';
    switch (status) {
      case 'open':
        return {
          key: 'upcoming',
          label: 'Anmeldung offen',
          color: 'text-[#3fced1]',
          bgColor: 'bg-[#3fced1]/10',
        };
      case 'running':
        return {
          key: 'active',
          label: 'Läuft',
          color: 'text-green-400',
          bgColor: 'bg-green-400/10',
        };
      case 'finished':
        return {
          key: 'finished',
          label: 'Beendet',
          color: 'text-gray-400',
          bgColor: 'bg-gray-400/10',
        };
      default:
        return {
          key: 'draft',
          label: 'Entwurf',
          color: 'text-gray-500',
          bgColor: 'bg-gray-500/10',
        };
    }
  }

  /** Registrierung offen? (Status open + registrationEnd nicht überschritten) */
  isRegistrationOpen(game: PredictionGame | null | undefined): boolean {
    if (!game || game.status !== 'open') return false;
    const end = this.parseDate(game.registrationEnd);
    if (!end) return true;
    return Date.now() < end.getTime();
  }

  /** Datum defensiv parsen (ISO-String, Number-Timestamp) */
  parseDate(value: string | number | null | undefined): Date | null {
    if (value === null || value === undefined || value === '') return null;
    try {
      const date = typeof value === 'number' ? new Date(value) : new Date(String(value));
      return Number.isNaN(date.getTime()) ? null : date;
    } catch {
      return null;
    }
  }

  /** "11.06.2026" */
  formatDate(value: string | number | null | undefined): string {
    const date = this.parseDate(value);
    if (!date) return '';
    return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }

  /** "11.06.2026, 20:00 Uhr" */
  formatDateTime(value: string | number | null | undefined): string {
    const date = this.parseDate(value);
    if (!date) return '';
    const datePart = date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    const timePart = date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
    return `${datePart}, ${timePart} Uhr`;
  }

  /** "20:00" */
  formatTime(value: string | number | null | undefined): string {
    const date = this.parseDate(value);
    if (!date) return '';
    return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  }

  /** "Mi, 11. Juni" */
  formatDayLabel(value: string | number | null | undefined): string {
    const date = this.parseDate(value);
    if (!date) return '';
    return date.toLocaleDateString('de-DE', { weekday: 'short', day: 'numeric', month: 'long' });
  }

  /** Zeitraum eines Tippspiels: "11.06.2026 – 14.06.2026" */
  formatGamePeriod(game: PredictionGame | null | undefined): string {
    if (!game) return '';
    const start = this.formatDate(game.gameStart);
    const end = this.formatDate(game.gameEnd);
    if (start && end) return `${start} – ${end}`;
    return start || end || '';
  }

  /** Tipp-Deadline eines Matches: startTime - matchDeadlineHours (default 1h, 0 erlaubt) */
  getMatchDeadline(fixture: PredictionFixture | null | undefined, game: PredictionGame | null | undefined): Date | null {
    const start = this.parseDate(fixture?.startTime);
    if (!start) return null;
    const hours = game?.settings?.matchDeadlineHours ?? 1;
    return new Date(start.getTime() - hours * 60 * 60 * 1000);
  }

  /** Darf für dieses Match noch getippt werden? */
  canPredictMatch(fixture: PredictionFixture | null | undefined, game: PredictionGame | null | undefined): boolean {
    const deadline = this.getMatchDeadline(fixture, game);
    if (!deadline) return false;
    const status = (fixture?.status || '').toLowerCase();
    if (status && status !== 'scheduled') return false;
    return Date.now() < deadline.getTime();
  }

  /** YYYY-MM-DD Key (lokal) für Datums-Gruppierung */
  getDateKey(value: string | number | null | undefined): string {
    const date = this.parseDate(value);
    if (!date) return '';
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  /** Spielername defensiv ("(XYZ)"-Suffixe entfernen) */
  cleanPlayerName(name: string | null | undefined): string {
    if (!name) return '';
    return name.replace(/\s*\([^)]*\)\s*$/, '').trim();
  }

  /** Initialen für Avatar-Fallback */
  getInitials(name: string | null | undefined): string {
    const clean = this.cleanPlayerName(name);
    if (!clean) return '?';
    return clean
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join('');
  }

  /** PlayerInfo-Felder normalisieren (id/playerId, name/playerName, image/playerImage) */
  /** Anzahl Legs, die der Sieger laut Match-Modus braucht (z.B. Best of 11 → 6). */
  getWinningLegs(fixture?: PredictionFixture | null): number | null {
    const wd = fixture?.matchFormat?.wd;
    if (typeof wd === 'number' && wd > 0) return wd;
    const bestOf = fixture?.matchFormat?.bestOf;
    if (typeof bestOf === 'number' && bestOf > 0) return Math.ceil(bestOf / 2);
    return null;
  }

  /** Modus-Label für Match-Cards, z.B. "Best of 11 Legs". */
  formatMatchFormat(fixture?: PredictionFixture | null): string | null {
    const bestOf = fixture?.matchFormat?.bestOf;
    if (typeof bestOf === 'number' && bestOf > 0) return `Best of ${bestOf} Legs`;
    const winning = this.getWinningLegs(fixture);
    if (winning != null) return `First to ${winning} Legs`;
    return null;
  }

  /**
   * Validiert einen Tipp gegen den Match-Modus (Best of 11 → Sieger braucht
   * genau 6 Legs, Verlierer 0–5). → Fehlertext oder null (gültig).
   */
  validatePredictedScore(fixture: PredictionFixture | null | undefined, home: number, away: number): string | null {
    if (Number.isNaN(home) || Number.isNaN(away) || home < 0 || away < 0) {
      return 'Bitte gib ein gültiges Ergebnis ein';
    }
    if (home === away) {
      return 'Bitte gib ein gültiges Ergebnis ein (kein Unentschieden möglich)';
    }
    const winning = this.getWinningLegs(fixture);
    if (winning == null) return null; // Modus unbekannt → keine strenge Prüfung
    const win = Math.max(home, away);
    const lose = Math.min(home, away);
    if (win !== winning || lose >= winning) {
      return `Ungültig für diesen Modus: Der Sieger braucht genau ${winning} Legs (z.B. ${winning}:${Math.max(0, Math.min(lose, winning - 1))})`;
    }
    return null;
  }

  /** Averages aus den permanent gecachten FlashLive-Stats (beendete Events). */
  getAveragesFromStats(fixture?: PredictionFixture | null): { home: string; away: string } | null {
    const list = fixture?.stats;
    if (!Array.isArray(list) || list.length === 0) return null;
    for (const entry of list) {
      const category = entry?.CATEGORY || '';
      if (category.includes('Durchschnitt') || category.includes('Average')) {
        const h = entry?.VALUE_HOME;
        const a = entry?.VALUE_AWAY;
        const hasHome = h && h !== '0.00' && h !== '';
        const hasAway = a && a !== '0.00' && a !== '';
        if (hasHome || hasAway) {
          return { home: hasHome ? String(h) : '-', away: hasAway ? String(a) : '-' };
        }
      }
    }
    return null;
  }

  /** "1 Punkt" / "3 Punkte" – korrekte Einzahl/Mehrzahl */
  formatPoints(points?: number | null): string {
    const n = points ?? 0;
    return n === 1 ? '1 Punkt' : `${n} Punkte`;
  }

  /** Bild-Pfad (z.B. /api/file/x.png oder Datei-ID) → vollständige URL */
  buildImageUrl(path?: string | null): string | null {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    if (path.startsWith('/')) return `${api.defaults.baseURL}${path}`;
    return `${api.defaults.baseURL}/api/file/${path}`;
  }

  normalizePlayerInfo(player: PredictionPlayerInfo | null | undefined): { id: string | null; name: string; image: string | null } {
    return {
      id: player?.id ?? player?.playerId ?? null,
      name: player?.name ?? player?.playerName ?? '',
      image: player?.image ?? player?.playerImage ?? null,
    };
  }

  /** Event-Referenz einer MatchPrediction → Event-ID */
  getPredictionEventId(prediction: MatchPrediction | null | undefined): string | null {
    const event = prediction?.event;
    if (!event) return null;
    if (typeof event === 'string') return event;
    return event._id ?? null;
  }

  /** Fehlermeldung aus Axios-Error extrahieren (Backend liefert deutsche Messages) */
  extractErrorMessage(error: any, fallback: string): string {
    const message = error?.response?.data?.message;
    if (typeof message === 'string' && message.length > 0) return message;
    if (typeof error?.message === 'string' && error.message.length > 0 && !error.message.startsWith('Request failed')) {
      return error.message;
    }
    return fallback;
  }
}

const predictionService = new PredictionService();
export default predictionService;
